import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AutoAwesome as NewOrdersIcon,
  Assignment as OpenOrdersIcon,
  ErrorSharp as OverdueOrdersIcon,
  LocalShipping as ReadyForReleaseIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { FeatureCard, Grid, Page, StatCard } from '@core/components';
import { useAuth } from '@core/contexts/AuthContext';
import { useAppLayout } from '@core/contexts/LayoutContext';
import { getNavLinkPath } from '@core/utils/routes';
import { getDashboardKpis } from '../../api/dashboard';
import DueDateCalendar from '../components/DueDateCalendar';
import ProductionNeedsAttentionCard from '../components/ProductionNeedsAttentionCard';
import { navItems } from '../navigation';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isWebTwo } = useAppLayout();
  const { user } = useAuth();
  const { data: kpis, isError } = useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: getDashboardKpis,
  });

  const hour = new Date().getHours();
  let timeOfDay: 'morning' | 'afternoon' | 'evening';
  if (hour >= 5 && hour < 12) {
    timeOfDay = 'morning';
  } else if (hour >= 12 && hour < 18) {
    timeOfDay = 'afternoon';
  } else {
    timeOfDay = 'evening';
  }
  const greeting = user?.firstName
    ? `Good ${timeOfDay}, ${user.firstName}!`
    : `Good ${timeOfDay}!`;

  const stats = [
    {
      title: 'New Orders',
      value: kpis?.newOrders ?? '—',
      icon: <NewOrdersIcon />,
      color: 'info' as const,
    },
    {
      title: 'Open Orders',
      value: kpis?.openOrders ?? '—',
      icon: <OpenOrdersIcon />,
      color: 'warning' as const,
    },
    {
      title: 'Overdue Orders',
      value: kpis?.overdueOrders ?? '—',
      icon: <OverdueOrdersIcon />,
      color: 'error' as const,
    },
    {
      title: 'Ready for Release',
      value: kpis?.readyForReleaseOrders ?? '—',
      icon: <ReadyForReleaseIcon />,
      color: 'success' as const,
    },
  ];

  const quickAccessPaths = useMemo(
    () =>
      [
        '/orders',
        '/production',
        '/invoices',
        '/inventory',
        '/customers',
        '/settings/*',
      ] as const,
    []
  );

  const features = useMemo(
    () =>
      quickAccessPaths
        .map((path) => navItems.find((item) => item.path === path))
        .filter((item): item is NonNullable<typeof item> => Boolean(item))
        .map((item) => ({
          title: item.title,
          description: item.subtitle ?? '',
          icon: item.icon,
          buttonText: `View ${item.title}`,
          onButtonClick: () => navigate(getNavLinkPath(item.path)),
        })),
    [navigate, quickAccessPaths]
  );

  return (
    <Page title={isWebTwo ? greeting : 'Dashboard'}>
      <Box maxWidth={1200} mx="auto" sx={{ flex: 1, p: 0.5 }}>
        <Grid columns={{ xs: 1, sm: 2, md: 4 }} gap={3} mb={4}>
          {stats.map((stat) => (
            <StatCard
              key={stat.title}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
              gradient={false}
            />
          ))}
        </Grid>

        <Grid
          gap={3}
          mb={4}
          sx={{
            gridTemplateColumns: {
              xs: '1fr',
              md: '5fr 3fr',
            },
          }}
        >
          <ProductionNeedsAttentionCard />
          <DueDateCalendar />
        </Grid>

        {isError && (
          <Typography color="error.main" variant="body2" mb={4}>
            Dashboard data is temporarily unavailable.
          </Typography>
        )}

        <Grid columns={{ xs: 1, md: 2, lg: 3 }} gap={3}>
          {features.map((feature) => (
            <FeatureCard
              key={feature.title}
              title={feature.title}
              description={feature.description}
              icon={feature.icon}
              buttonText={feature.buttonText}
              onButtonClick={feature.onButtonClick}
            />
          ))}
        </Grid>
      </Box>
    </Page>
  );
};

export default Dashboard;
